import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { paymentsAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { motion } from 'framer-motion';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }
    pollStatus();
  }, [sessionId]);

  const pollStatus = async () => {
    const maxAttempts = 8;
    const poll = async (attempt) => {
      if (attempt >= maxAttempts) {
        setStatus('timeout');
        return;
      }
      try {
        const res = await paymentsAPI.status(sessionId);
        const data = res.data;
        if (data.payment_status === 'paid') {
          setStatus('success');
          if (refreshUser) await refreshUser();
          return;
        } else if (data.status === 'expired') {
          setStatus('expired');
          return;
        }
        setAttempts(attempt + 1);
        setTimeout(() => poll(attempt + 1), 2000);
      } catch {
        setAttempts(attempt + 1);
        setTimeout(() => poll(attempt + 1), 2000);
      }
    };
    poll(0);
  };

  return (
    <div className="min-h-screen min-h-[100dvh] bg-[#F8FAFC] flex items-center justify-center px-6 safe-top">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white border border-[#E2E8F0] rounded-2xl p-8 max-w-sm w-full text-center shadow-lg"
      >
        {status === 'checking' && (
          <>
            <Loader2 size={48} className="text-[#2563EB] animate-spin mx-auto mb-4" />
            <h2 className="font-['Barlow_Condensed'] text-xl font-bold text-[#0A2242] mb-2">CONFIRMING PAYMENT</h2>
            <p className="text-sm text-[#64748B]">Verifying your subscription...</p>
            <div className="mt-4 flex justify-center gap-1">
              {[...Array(8)].map((_, i) => (
                <div key={i} className={`w-2 h-2 rounded-full transition-colors ${i <= attempts ? 'bg-[#2563EB]' : 'bg-[#E2E8F0]'}`} />
              ))}
            </div>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-16 h-16 rounded-full bg-[#10B981]/10 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 size={36} className="text-[#10B981]" />
            </div>
            <h2 className="font-['Barlow_Condensed'] text-2xl font-bold text-[#0A2242] mb-2" data-testid="payment-success-title">
              YOU'RE IN!
            </h2>
            <p className="text-sm text-[#64748B] mb-6">
              Welcome to Elev802 Connected. Let's set up your player profile.
            </p>
            <button
              onClick={() => navigate('/onboarding')}
              className="w-full bg-[#0A2242] text-white font-bold uppercase tracking-wide rounded-xl py-4 transition-colors hover:bg-[#0E2D5A] text-sm"
              data-testid="start-training-btn"
            >
              Set Up My Profile
            </button>
          </>
        )}

        {(status === 'expired' || status === 'error' || status === 'timeout') && (
          <>
            <div className="w-16 h-16 rounded-full bg-[#EF4444]/10 flex items-center justify-center mx-auto mb-4">
              <XCircle size={36} className="text-[#EF4444]" />
            </div>
            <h2 className="font-['Barlow_Condensed'] text-xl font-bold text-[#0A2242] mb-2">
              {status === 'timeout' ? 'STILL PROCESSING' : 'PAYMENT ISSUE'}
            </h2>
            <p className="text-sm text-[#64748B] mb-6">
              {status === 'timeout'
                ? "Your payment is still being processed. It should activate within a few minutes."
                : "Something went wrong. Please try again or contact support."}
            </p>
            <button
              onClick={() => navigate('/dashboard')}
              className="w-full bg-[#0A2242] text-white font-bold uppercase tracking-wide rounded-xl py-4 transition-colors hover:bg-[#0E2D5A] text-sm"
            >
              Back to Dashboard
            </button>
          </>
        )}
      </motion.div>
    </div>
  );
}
